import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import { copyTextToClipboard, exportAdminTableAsCsv, exportAdminTableAsJson, normalizeAdminExportRows } from '../../admin/adminExportUtils';
import { filterAdminTableRows, nextSortState, sortAdminTableRows } from '../../admin/adminTableClient';

export default function AdminPlatformReadinessPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [search, setSearch] = useState('');
  const [fOk, setFOk] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_PLATFORM_READINESS), {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('bad');
        const json = await res.json();
        if (!cancel) {
          setData(json);
          setErr(false);
        }
      } catch {
        if (!cancel) setErr(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const baseRows = useMemo(() => {
    const hints = data?.envHints || {};
    return Object.keys(hints).map((k) => ({
      __rowKey: k,
      env_key: k,
      configured_label: hints[k] ? t('adminKit.configured') : t('adminKit.missing'),
      configured_raw: hints[k] ? 'yes' : 'no',
    }));
  }, [data, t]);

  const filterKeys = useMemo(() => ['env_key', 'configured_label', 'configured_raw'], []);

  const displayRows = useMemo(() => {
    let out = filterAdminTableRows(baseRows, filterKeys, search, {});
    const ok = (fOk || '').trim().toLowerCase();
    if (ok === 'yes') out = out.filter((r) => r.configured_raw === 'yes');
    else if (ok === 'no') out = out.filter((r) => r.configured_raw === 'no');
    return sortAdminTableRows(out, sortKey, sortDir);
  }, [baseRows, filterKeys, search, fOk, sortKey, sortDir]);

  const handleSort = useCallback(
    (key) => {
      const n = nextSortState(sortKey, sortDir, key);
      setSortKey(n.key);
      setSortDir(n.dir);
    },
    [sortKey, sortDir]
  );

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navPlatform'), to: '/admin/platform/readiness' },
      { label: t('adminHub.navServiceReadiness') },
    ],
    [t]
  );

  const okOptions = useMemo(
    () => [
      { value: '', label: t('adminExport.filterAll') },
      { value: 'yes', label: t('adminExport.readinessConfigured') },
      { value: 'no', label: t('adminExport.readinessMissing') },
    ],
    [t]
  );

  const displayCols = useMemo(
    () => [
      { key: 'env_key', header: t('adminKit.colFlagKey') },
      { key: 'configured_label', header: t('adminKit.colReadiness') },
    ],
    [t]
  );

  const exportKeys = ['env_key', 'configured_raw'];
  const hasFilters = !!(search.trim() || (fOk || '').trim());

  return (
    <AdminHubPageFrame titleKey="adminHub.readinessTitle" descKey="adminHub.readinessDesc" breadcrumbItems={crumbs}>
      <section className="admin-section-card">
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : !data ? (
          <div className="admin-state admin-state--loading" role="status">
            <p>{t('adminPage.loading')}</p>
          </div>
        ) : (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminKit.readinessGridTitle')} subtitle={t('adminKit.readinessGridSub')} />
            <AdminTableToolbar
              search={search}
              onSearchChange={setSearch}
              filteredCount={displayRows.length}
              totalCount={baseRows.length}
              compact={compact}
              onCompactChange={setCompact}
              onExportCsv={() => exportAdminTableAsCsv(displayRows, exportKeys, 'platform-readiness')}
              onExportJson={() => exportAdminTableAsJson(displayRows, exportKeys, 'platform-readiness')}
              onCopyFilteredJson={() => copyTextToClipboard(JSON.stringify(normalizeAdminExportRows(displayRows, exportKeys)))}
              filters={[
                {
                  id: 'ok',
                  label: t('adminKit.colReadiness'),
                  value: fOk,
                  onChange: setFOk,
                  options: okOptions,
                },
              ]}
            />
            <AdminDataTable
              columns={displayCols}
              rows={displayRows}
              emptyMessage={hasFilters ? t('adminExport.noResults') : t('adminKit.tableEmpty')}
              emptyVariant={hasFilters ? 'noResults' : 'default'}
              compact={compact}
              sortable
              sortKey={sortKey}
              sortDir={sortDir}
              onSortChange={handleSort}
              copyRowKeys={exportKeys}
            />
            {data.note ? <p className="admin-hub-kit-note">{data.note}</p> : null}
            {data.arsheef_commercial_bridge ? (
              <div className="admin-hub-kit-stack" style={{ marginTop: 12 }}>
                <AdminSectionHeader
                  title="Arsheef ↔ Ydim commercial bridge"
                  subtitle="Sprint 3.5–3.6 — snapshot + optional office subscription sync (readiness only; no secrets)."
                />
                <AdminJsonDetails title="Arsheef commercial bridge (subset)" data={data.arsheef_commercial_bridge} />
              </div>
            ) : null}
            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data} />
          </div>
        )}
      </section>
    </AdminHubPageFrame>
  );
}
